import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { NonCustomCategories } from "@/shared/constants/fee-schedule-categories";

export enum PROCEDURES_PICKER_TABS {
  ICD10 = "icd10",
  CPT = "cpt",
}

/** A most common section listed from a seeded ValueSet. */
export type ValueSetSection = { title: string; valueSet: string };
/** A quick picker section: the codes of one fee schedule CodeSystem flagged for the quick picker. */
export type QuickPickerSection = { title: NonCustomCategories; codeSystem: string };
export type MostCommonSectionConfig = ValueSetSection | QuickPickerSection;

/** CodeSystem concept property that puts a fee schedule code on the CPT quick picker. */
export const QUICK_PICKER_PROPERTY = "quick-picker";

const feeScheduleCodeSystem = (slug: string) => `${Systems.QH}/fhir/CodeSystem/${slug}`;

// The CPT tab lists the fee schedule codes flagged with QUICK_PICKER_PROPERTY, one section per fee
// schedule category in the Fee Table's order, so names, Info box text and order match the Fee
// Table. Prolonged Services has no quick picker codes, so it has no section.
export const cptQuickPickerSections: QuickPickerSection[] = [
  {
    title: NonCustomCategories.LaborAndDelivery,
    codeSystem: feeScheduleCodeSystem("global-maternity-care"),
  },
  {
    title: NonCustomCategories.OfficeVisitNewPatient,
    codeSystem: feeScheduleCodeSystem("office-visit-new-client"),
  },
  {
    title: NonCustomCategories.OfficeVisitEstablishedPatient,
    codeSystem: feeScheduleCodeSystem("office-visit-established-client"),
  },
  {
    title: NonCustomCategories.OfficeVisitConsultation,
    codeSystem: feeScheduleCodeSystem("office-visit-consultation"),
  },
  {
    title: NonCustomCategories.HomeVisitNewPatient,
    codeSystem: feeScheduleCodeSystem("home-visit-new-client"),
  },
  {
    title: NonCustomCategories.HomeVisitEstablishedPatient,
    codeSystem: feeScheduleCodeSystem("home-visit-established-client"),
  },
  {
    title: NonCustomCategories.FetalTestingAndUltrasound,
    codeSystem: feeScheduleCodeSystem("nst-us-doppler"),
  },
  {
    title: NonCustomCategories.PreventiveVisitNewPatient,
    codeSystem: feeScheduleCodeSystem("well-woman-new-client"),
  },
  {
    title: NonCustomCategories.PreventiveVisitEstablishedPatient,
    codeSystem: feeScheduleCodeSystem("well-woman-established-client"),
  },
  {
    title: NonCustomCategories.GynecologyAndContraception,
    codeSystem: feeScheduleCodeSystem("well-woman-other"),
  },
  {
    title: NonCustomCategories.NewbornPediatricCare,
    codeSystem: feeScheduleCodeSystem("newborn-care"),
  },
  {
    title: NonCustomCategories.MedicationsCathetersIVOxygen,
    codeSystem: feeScheduleCodeSystem("medications-iv-catheters-oxygen"),
  },
  {
    title: NonCustomCategories.OtherProceduresAndServices,
    codeSystem: feeScheduleCodeSystem("other-procedures-and-services"),
  },
  { title: NonCustomCategories.FacilityFee, codeSystem: feeScheduleCodeSystem("facility-fee") },
  {
    title: NonCustomCategories.ScreeningCounseling,
    codeSystem: feeScheduleCodeSystem("screening-counseling"),
  },
  {
    title: NonCustomCategories.TelehealthVisits,
    codeSystem: feeScheduleCodeSystem("audio-visual-visits"),
  },
];

// Category ValueSets from the ICD-10 flagged-codes spreadsheet. Titles mirror the `name` of the
// seeded ValueSet resources (app/fhir/seed-data/value-sets/). These ValueSets hold only ICD-10-CM
// codes, because a code checked in the CPT tab is saved as a billable Procedure.
export const icd10MostCommonValueSets: ValueSetSection[] = [
  { title: "Pregnancy Supervision", valueSet: QuiltedHealthValueSets.PregnancySupervision },
  {
    title: "Prenatal Screening & Counseling",
    valueSet: QuiltedHealthValueSets.PrenatalScreeningCounseling,
  },
  { title: "Prenatal Conditions", valueSet: QuiltedHealthValueSets.PrenatalConditions },
  { title: "High-Risk / Complications", valueSet: QuiltedHealthValueSets.HighRiskComplications },
  {
    title: "Labor & Birth",
    valueSet: QuiltedHealthValueSets.LaborDeliveryEncounters,
  },
  { title: "Postpartum and Lactation", valueSet: QuiltedHealthValueSets.PostpartumICDCodes },
  { title: "Well Newborn Encounters", valueSet: QuiltedHealthValueSets.WellNewbornEncounters },
  { title: "Newborn Conditions", valueSet: QuiltedHealthValueSets.NewbornConditions },
  { title: "Contraception", valueSet: QuiltedHealthValueSets.ContraceptionDiagnoses },
  { title: "Gyn", valueSet: QuiltedHealthValueSets.Gyn },
];

// FHIR coding systems used when persisting the picker's selections directly as
// Condition and Procedure resources. Kept in sync with the encounter-procedures bot
// (app/bots/encounter/encounter-procedures/encounter-procedures.ts) so both writers
// produce interchangeable resources.
export enum ProceduresPickerSystems {
  ConditionCategory = "http://terminology.hl7.org/CodeSystem/condition-category",
  ConditionClinicalStatus = "http://terminology.hl7.org/CodeSystem/condition-clinical",
  ConditionVerificationStatus = "http://terminology.hl7.org/CodeSystem/condition-ver-status",
  EncounterCodeOrigin = "http://quiltedhealth.com/fhir/CodeSystem/encounter-code-origin",
  BillingCodeAuthor = "http://quiltedhealth.com/fhir/CodeSystem/billing-code-author",
  NonClinicalData = "http://quiltedhealth.com/fhir/CodeSystem/qh-non-clinical-data",
}

// Removing a code from the picker retires the resource by status instead of deleting it, so the
// encounter keeps a record of what was entered in error. Both the reads and the writes filter on
// this code: Procedure through `status`, Condition through `verificationStatus`.
export const PROCEDURES_ENTERED_IN_ERROR = "entered-in-error";

export enum ProceduresPickerCategoryCodes {
  EncounterDiagnosis = "procedure-diagnosis",
  EncounterCreated = "encounter-created",
  Clinical = "clinical",
  Billing = "billing",
  /**
   * Carried by everything entered through the billing tab's biller section, whoever entered it.
   *
   * It is what keeps the two entry points apart: the procedures tab records the encounter's clinical
   * work, the biller section records what is being billed for on top of it, and neither reconciles
   * against the other's resources. See {@link EncounterProceduresScope}.
   */
  NonClinicalData = "non-clinical-data",
}

/**
 * Which side of an encounter's picker-managed resources a read or a write is about.
 *
 * Named at every call site rather than defaulted: the three callers want three different answers —
 * the procedures tab only the clinical side, the biller section only its own, and the billing and
 * charge-capture reads all of it — and a caller that inherited the wrong one would fail quietly.
 */
export enum EncounterProceduresScope {
  All = "all",
  Clinical = "clinical",
  NonClinical = "non-clinical",
}
